/**
 * Ayudas minimas sobre el DOM.
 *
 * No hay framework a proposito: el panel es un formulario y cuatro listas, y
 * meter una libreria de interfaz solo anadiria peso a algo que tiene que
 * dejarle toda la maquina al lienzo.
 *
 * Todo el texto que viene de los datos se inserta con `textContent`, nunca
 * concatenando HTML: un nombre con un `<` dentro no puede romper nada.
 */

export function el<K extends keyof HTMLElementTagNameMap>(
  etiqueta: K,
  opciones: {
    clase?: string;
    texto?: string;
    atrib?: Record<string, string>;
    hijos?: (Node | null | undefined)[];
  } = {},
): HTMLElementTagNameMap[K] {
  const n = document.createElement(etiqueta);
  if (opciones.clase) n.className = opciones.clase;
  if (opciones.texto !== undefined) n.textContent = opciones.texto;
  for (const [k, v] of Object.entries(opciones.atrib ?? {})) n.setAttribute(k, v);
  for (const h of opciones.hijos ?? []) if (h) n.appendChild(h);
  return n;
}

export function buscar<T extends HTMLElement>(selector: string): T {
  const n = document.querySelector<T>(selector);
  if (!n) throw new Error(`no existe el elemento ${selector}`);
  return n;
}

export function vaciar(n: HTMLElement): void {
  while (n.firstChild) n.removeChild(n.firstChild);
}

/** Un par etiqueta/valor para las fichas. */
export function dato(etiqueta: string, valor: Node | string): [HTMLElement, HTMLElement] {
  const dt = el("dt", { texto: etiqueta });
  const dd = el("dd");
  if (typeof valor === "string") dd.textContent = valor;
  else dd.appendChild(valor);
  return [dt, dd];
}

/** Un boton que lleva a una persona. */
export function botonPersona(
  nombre: string,
  relacion: string | undefined,
  alPulsar: () => void,
): HTMLElement {
  const b = el("button", { clase: "persona" });
  b.appendChild(el("span", { texto: nombre }));
  if (relacion) b.appendChild(el("span", { clase: "rel", texto: relacion }));
  b.addEventListener("click", alPulsar);
  return el("li", { hijos: [b] });
}

export interface OpcionSelect {
  valor: string;
  texto: string;
}

export function selectDe(
  opciones: OpcionSelect[],
  valor: string | undefined,
  alCambiar?: (v: string) => void,
): HTMLSelectElement {
  const s = el("select");
  for (const o of opciones) {
    const op = el("option", { texto: o.texto });
    op.value = o.valor;
    s.appendChild(op);
  }
  if (valor !== undefined) s.value = valor;
  if (alCambiar) s.addEventListener("change", () => alCambiar(s.value));
  return s;
}

export function campo(
  etiqueta: string,
  control: HTMLElement,
  pista?: string,
): HTMLElement {
  return el("div", {
    clase: "campo",
    hijos: [
      el("label", { texto: etiqueta }),
      control,
      pista ? el("div", { clase: "pista", texto: pista }) : null,
    ],
  });
}

export function entrada(
  valor: string | undefined,
  marcador?: string,
  tipo = "text",
): HTMLInputElement {
  const i = el("input");
  i.type = tipo;
  i.value = valor ?? "";
  if (marcador) i.placeholder = marcador;
  return i;
}
